import {
  Badge,
  Box,
  Button,
  Grid,
  HStack,
  Icon,
  Input,
  Text,
  VStack
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiExternalLink,
  FiHash,
  FiInfo,
  FiLink,
  FiNavigation,
  FiRefreshCw,
  FiSearch,
  FiShuffle
} from "react-icons/fi";
import { ABTestPaths } from "./ABTestPaths";

interface DestinationSectionProps {
  domain: string;
  destinationType: "single" | "ab-test" | "variant" | "custom";
  destinationPath: string;
  destinationVariant: string;
  customRedirectUrl?: string;
  abTestPaths: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule: {
    enabled: boolean;
    startDate: string;
    endDate: string;
  };
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
}

// Available paths for dropdowns
const availablePaths = [
  {
    value: "/seller",
    label: "/seller",
    description: "Seller landing page"
  },
  {
    value: "/buyer",
    label: "/buyer",
    description: "Buyer landing page"
  }
];

// Available website variants
const availableVariants = [
  {
    value: "241211212116",
    label: "241211212116",
    description: "Seller - www/zown-ca-lts/seller/241211212116"
  },
  {
    value: "49504711948",
    label: "49504711948",
    description: "Buyer - www/zown-ca-lts/airbnb-inspired-page/49504711948"
  }
];

// SearchableDropdown Component
interface SearchableDropdownProps {
  value: string;
  placeholder: string;
  options: any[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  disabled: boolean;
  isMonospace?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  placeholder,
  options,
  isOpen,
  onToggle,
  onSelect,
  searchQuery,
  onSearchChange,
  disabled,
  isMonospace = false
}) => (
  <>
    <Box
      as="button"
      w="100%"
      p={3}
      bg="white"
      border="1px solid"
      borderColor={isOpen ? "blue.500" : "gray.300"}
      borderRadius="md"
      textAlign="left"
      fontSize="md"
      aria-disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      _hover={{ borderColor: "gray.400" }}
      _focus={{
        borderColor: "blue.500",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
        outline: "none"
      }}
      _disabled={{
        opacity: 0.6,
        cursor: "not-allowed"
      }}
    >
      <HStack justify="space-between">
        <Text
          color={value ? "black" : "gray.500"}
          fontFamily={isMonospace && value ? "mono" : "inherit"}
        >
          {value || placeholder}
        </Text>
        <Icon
          as={FiChevronDown}
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
          transition="transform 0.2s"
        />
      </HStack>
    </Box>

    {isOpen && (
      <Box
        position="absolute"
        top="100%"
        left={0}
        right={0}
        mt={1}
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        shadow="lg"
        zIndex={10}
        maxH="400px"
        overflow="hidden"
      >
        <Box p={3} borderBottom="1px solid" borderColor="gray.200">
          <HStack>
            <Icon as={FiSearch} color="gray.400" />
            <Input
              placeholder={`Search ${isMonospace ? "variants" : "paths"}...`}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              size="sm"
              border="none"
              _focus={{
                outline: "none",
                boxShadow: "none"
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </HStack>
        </Box>

        <Box maxH="300px" overflowY="auto">
          {options.length > 0 ? (
            options.map((option: any) => (
              <Box
                key={option.value}
                as="button"
                w="100%"
                p={3}
                textAlign="left"
                bg={value === option.value ? "blue.50" : "white"}
                _hover={{ bg: "gray.50" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(option.value);
                }}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <HStack justify="space-between" align="start">
                  <VStack align="start" gap={0}>
                    <Text
                      fontSize="sm"
                      fontWeight={value === option.value ? "medium" : "normal"}
                      fontFamily={isMonospace ? "mono" : "inherit"}
                    >
                      {option.label}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {option.description}
                    </Text>
                  </VStack>
                  {value === option.value && (
                    <Icon as={FiCheck} color="blue.500" />
                  )}
                </HStack>
              </Box>
            ))
          ) : (
            <Box p={4} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                No {isMonospace ? "variants" : "paths"} found matching "
                {searchQuery}"
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    )}
  </>
);

// URL validation function
const validateUrl = (url: string): { isValid: boolean; error?: string } => {
  if (!url) {
    return { isValid: false, error: "URL is required" };
  }
  
  try {
    const urlObj = new URL(url);
    
    // Check for valid protocol
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { isValid: false, error: "URL must start with http:// or https://" };
    }
    
    // Check for valid hostname
    if (!urlObj.hostname || urlObj.hostname.length < 3) {
      return { isValid: false, error: "Invalid domain name" };
    }
    
    // Check for common typos
    if (urlObj.hostname.includes('..') || urlObj.hostname.startsWith('.') || urlObj.hostname.endsWith('.')) {
      return { isValid: false, error: "Invalid domain format" };
    }
    
    return { isValid: true };
  } catch (error) {
    return { isValid: false, error: "Invalid URL format. Please enter a valid URL (e.g., https://example.com)" };
  }
};

export const DestinationSection: React.FC<DestinationSectionProps> = ({
  domain,
  destinationType,
  destinationPath,
  destinationVariant,
  customRedirectUrl = "",
  abTestPaths,
  abTestSchedule,
  onChange,
  disabled = false
}) => {
  const [pathSearchQuery, setPathSearchQuery] = useState("");
  const [isPathDropdownOpen, setIsPathDropdownOpen] = useState(false);
  const [variantSearchQuery, setVariantSearchQuery] = useState("");
  const [isVariantDropdownOpen, setIsVariantDropdownOpen] = useState(false);
  const [abTestDropdowns, setAbTestDropdowns] = useState<{
    [key: string]: { isOpen: boolean; searchQuery: string };
  }>({});
  const [urlValidation, setUrlValidation] = useState<{ isValid: boolean; error?: string }>({ isValid: true });
  const [isRefreshingPath, setIsRefreshingPath] = useState(false);
  const [isRefreshingVariant, setIsRefreshingVariant] = useState(false);

  // Validate custom URL on mount and when it changes from external source
  useEffect(() => {
    if (destinationType === "custom" && customRedirectUrl) {
      const validation = validateUrl(customRedirectUrl);
      setUrlValidation(validation);
    }
  }, [destinationType, customRedirectUrl]);

  // Get main domain from tracking domain
  const getMainDomain = (trackingDomain: string) => {
    if (trackingDomain.includes("zown.ca")) {
      return "zown.ca";
    } else if (trackingDomain.includes("zownhomes.com")) {
      return "zownhomes.com";
    }
    return "zownhomes.com"; // Default fallback
  };

  // Filter paths based on search query
  const filteredPaths = availablePaths.filter(
    (path) =>
      path.value.toLowerCase().includes(pathSearchQuery.toLowerCase()) ||
      path.description.toLowerCase().includes(pathSearchQuery.toLowerCase())
  );

  // Filter variants based on search query
  const filteredVariants = availableVariants.filter(
    (variant) =>
      variant.value.toLowerCase().includes(variantSearchQuery.toLowerCase()) ||
      variant.description
        .toLowerCase()
        .includes(variantSearchQuery.toLowerCase())
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (isPathDropdownOpen && !target.closest("[data-path-dropdown]")) {
        setIsPathDropdownOpen(false);
        setPathSearchQuery("");
      }

      if (isVariantDropdownOpen && !target.closest("[data-variant-dropdown]")) {
        setIsVariantDropdownOpen(false);
        setVariantSearchQuery("");
      }

      Object.entries(abTestDropdowns).forEach(([pathId, dropdown]) => {
        if (
          dropdown.isOpen &&
          !target.closest(`[data-ab-dropdown="${pathId}"]`)
        ) {
          setAbTestDropdowns((prev) => ({
            ...prev,
            [pathId]: { ...prev[pathId], isOpen: false, searchQuery: "" }
          }));
        }
      });
    };

    const hasOpenDropdowns =
      isPathDropdownOpen ||
      isVariantDropdownOpen ||
      Object.values(abTestDropdowns).some((d) => d.isOpen);

    if (hasOpenDropdowns) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isPathDropdownOpen, isVariantDropdownOpen, abTestDropdowns]);

  return (
    <Box bg="white" shadow="sm" rounded="lg" p={6}>
      <HStack mb={4}>
        <Icon as={FiNavigation} color="blue.500" boxSize={5} />
        <Text fontWeight="semibold" fontSize="lg">
          Destination
        </Text>
        <Badge colorScheme="red" size="sm">
          Required
        </Badge>
      </HStack>

      <VStack gap={4} align="stretch">
        {/* Destination Type Toggle */}
        <Box>
          <HStack mb={3}>
            <Text fontSize="sm" fontWeight="medium">
              Destination Type
            </Text>
          </HStack>
          <HStack gap={2} wrap="wrap">
            <Button
              type="button"
              size="sm"
              variant={destinationType === "single" ? "solid" : "outline"}
              colorScheme={destinationType === "single" ? "blue" : "gray"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("destinationType", "single");
              }}
              disabled={disabled}
            >
              <HStack gap={1}>
                <Icon as={FiLink} boxSize={4} />
                <Text>Single Path</Text>
              </HStack>
            </Button>
            <Button
              type="button"
              size="sm"
              variant={destinationType === "ab-test" ? "solid" : "outline"}
              colorScheme={destinationType === "ab-test" ? "blue" : "gray"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("destinationType", "ab-test");
              }}
              disabled={disabled}
            >
              <HStack gap={1}>
                <Icon as={FiShuffle} boxSize={4} />
                <Text>A/B Test</Text>
              </HStack>
            </Button>
            <Button
              type="button"
              size="sm"
              variant={destinationType === "variant" ? "solid" : "outline"}
              colorScheme={destinationType === "variant" ? "blue" : "gray"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("destinationType", "variant");
              }}
              disabled={disabled}
            >
              <HStack gap={1}>
                <Icon as={FiHash} boxSize={4} />
                <Text>Variant ID</Text>
              </HStack>
            </Button>
            <Button
              type="button"
              size="sm"
              variant={destinationType === "custom" ? "solid" : "outline"}
              colorScheme={destinationType === "custom" ? "blue" : "gray"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("destinationType", "custom");
              }}
              disabled={disabled}
            >
              <HStack gap={1}>
                <Icon as={FiExternalLink} boxSize={4} />
                <Text>Custom URL</Text>
              </HStack>
            </Button>
          </HStack>
        </Box>

        {/* Single Path Selector */}
        {destinationType === "single" && (
          <Box>
            <HStack mb={2}>
              <Text fontSize="sm" fontWeight="medium">
                Website Path
              </Text>
            </HStack>
            <HStack>
              <Text fontSize="sm" color="gray.600" minW="fit-content">
                https://{getMainDomain(domain)}
              </Text>
              <Box position="relative" flex={1} data-path-dropdown>
                <SearchableDropdown
                  value={destinationPath}
                  placeholder="Select a path..."
                  options={filteredPaths}
                  isOpen={isPathDropdownOpen}
                  onToggle={() => {
                    setIsPathDropdownOpen(!isPathDropdownOpen);
                    setPathSearchQuery("");
                  }}
                  onSelect={(value) => {
                    onChange("destinationPath", value);
                    setIsPathDropdownOpen(false);
                    setPathSearchQuery("");
                  }}
                  searchQuery={pathSearchQuery}
                  onSearchChange={setPathSearchQuery}
                  disabled={disabled}
                />
              </Box>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsRefreshingPath(true);
                  // Reset search and close dropdown to refresh the list
                  setPathSearchQuery("");
                  setIsPathDropdownOpen(false);
                  // Small delay then reopen to show refreshed list
                  setTimeout(() => {
                    setIsPathDropdownOpen(true);
                    setIsRefreshingPath(false);
                  }, 500);
                }}
                disabled={disabled || isRefreshingPath}
                aria-label="Refresh path list"
                title="Refresh path list"
                px={2}
                _hover={{ bg: "gray.100" }}
              >
                <Icon 
                  as={FiRefreshCw} 
                  boxSize={4} 
                  className={isRefreshingPath ? "animate-spin" : ""} 
                />
              </Button>
            </HStack>
            <Text fontSize="sm" color="gray.500" mt={2}>
              Select from available paths or search by path name or description
            </Text>
          </Box>
        )}

        {/* Variant Selector */}
        {destinationType === "variant" && (
          <Box>
            <HStack mb={2}>
              <Text fontSize="sm" fontWeight="medium">
                Website Variant ID
              </Text>
            </HStack>
            <HStack>
              <Box position="relative" flex={1} data-variant-dropdown>
                <SearchableDropdown
                  value={destinationVariant}
                  placeholder="Select a variant..."
                  options={filteredVariants}
                  isOpen={isVariantDropdownOpen}
                  onToggle={() => {
                    setIsVariantDropdownOpen(!isVariantDropdownOpen);
                    setVariantSearchQuery("");
                  }}
                  onSelect={(value) => {
                    onChange("destinationVariant", value);
                    setIsVariantDropdownOpen(false);
                    setVariantSearchQuery("");
                  }}
                  searchQuery={variantSearchQuery}
                  onSearchChange={setVariantSearchQuery}
                  disabled={disabled}
                  isMonospace={true}
                />
              </Box>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsRefreshingVariant(true);
                  // Reset search and close dropdown to refresh the list
                  setVariantSearchQuery("");
                  setIsVariantDropdownOpen(false);
                  // Small delay then reopen to show refreshed list
                  setTimeout(() => {
                    setIsVariantDropdownOpen(true);
                    setIsRefreshingVariant(false);
                  }, 500);
                }}
                disabled={disabled || isRefreshingVariant}
                aria-label="Refresh variant list"
                title="Refresh variant list"
                px={2}
                _hover={{ bg: "gray.100" }}
              >
                <Icon 
                  as={FiRefreshCw} 
                  boxSize={4} 
                  className={isRefreshingVariant ? "animate-spin" : ""} 
                />
              </Button>
            </HStack>
            <Text fontSize="sm" color="gray.500" mt={2}>
              Select a specific website variant by its ID for precise version control
            </Text>

            <Box
              mt={3}
              p={3}
              bg="purple.50"
              rounded="md"
              borderLeft="3px solid"
              borderColor="purple.400"
            >
              <HStack gap={2} align="start">
                <Icon as={FiInfo} color="purple.600" boxSize={4} mt={0.5} />
                <VStack align="start" gap={1}>
                  <Text fontSize="xs" color="purple.800" fontWeight="medium">
                    About Variant IDs:
                  </Text>
                  <Text fontSize="xs" color="purple.700">
                    • Each variant represents a specific version of a page
                  </Text>
                  <Text fontSize="xs" color="purple.700">
                    • IDs are generated by your CMS or testing platform
                  </Text>
                  <Text fontSize="xs" color="purple.700">
                    • Perfect for multivariate testing and version control
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </Box>
        )}

        {/* A/B Test Configuration */}
        {destinationType === "ab-test" && (
          <>
            {/* Schedule Settings - Matching CreateABTest page */}
            <VStack gap={4} align="stretch">
              {/* Enable Scheduling Toggle */}
              <Box>
                <HStack mb={3}>
                  <Text fontSize="sm" fontWeight="medium">
                    Auto-schedule test activation
                  </Text>
                  <Box
                    position="relative"
                    display="inline-block"
                    width="44px"
                    height="24px"
                    bg={abTestSchedule.enabled ? "blue.500" : "gray.300"}
                    borderRadius="full"
                    transition="all 0.3s"
                    cursor={disabled ? "not-allowed" : "pointer"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!disabled) {
                        if (!abTestSchedule.enabled) {
                          // Enabling: set start date to current time
                          const now = new Date();
                          const year = now.getFullYear();
                          const month = String(now.getMonth() + 1).padStart(2, "0");
                          const day = String(now.getDate()).padStart(2, "0");
                          const hours = String(now.getHours()).padStart(2, "0");
                          const minutes = String(now.getMinutes()).padStart(2, "0");
                          const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
                          
                          onChange("abTestSchedule", {
                            enabled: true,
                            startDate: currentDateTime,
                            endDate: ""
                          });
                        } else {
                          // Disabling: reset all dates
                          onChange("abTestSchedule", {
                            enabled: false,
                            startDate: "",
                            endDate: ""
                          });
                        }
                      }
                    }}
                    _hover={{
                      bg: abTestSchedule.enabled ? "blue.600" : "gray.400"
                    }}
                    _after={{
                      content: '""',
                      position: "absolute",
                      width: "20px",
                      height: "20px",
                      borderRadius: "full",
                      bg: "white",
                      top: "2px",
                      left: abTestSchedule.enabled ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}
                  />
                </HStack>
                {abTestSchedule.enabled && (
                  <>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                      <Box>
                        <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.700">
                          Start Date & Time
                        </Text>
                        <Input
                          type="datetime-local"
                          value={abTestSchedule.startDate}
                          onChange={(e) =>
                            onChange("abTestSchedule", {
                              ...abTestSchedule,
                              startDate: e.target.value
                            })
                          }
                          size="sm"
                          disabled={disabled}
                          min={new Date().toISOString().slice(0, 16)}
                        />
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.700">
                          End Date & Time (Optional)
                        </Text>
                        <Input
                          type="datetime-local"
                          value={abTestSchedule.endDate}
                          onChange={(e) =>
                            onChange("abTestSchedule", {
                              ...abTestSchedule,
                              endDate: e.target.value
                            })
                          }
                          size="sm"
                          disabled={disabled}
                          min={abTestSchedule.startDate || new Date().toISOString().slice(0, 16)}
                        />
                      </Box>
                    </Grid>

                    {/* Info Boxes from CreateABTest */}
                    {abTestSchedule.startDate && !abTestSchedule.endDate && (
                      <Box
                        p={3}
                        bg="blue.50"
                        rounded="md"
                        borderLeft="3px solid"
                        borderColor="blue.400"
                        mb={3}
                      >
                        <HStack gap={2} align="start">
                          <Icon as={FiInfo} color="blue.600" boxSize={4} mt={0.5} />
                          <VStack align="start" gap={1}>
                            <Text fontSize="xs" color="blue.800" fontWeight="medium">
                              Test will run indefinitely
                            </Text>
                            <Text fontSize="xs" color="blue.700">
                              The test will start at the scheduled time and continue until manually stopped
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    )}

                    {abTestSchedule.startDate && abTestSchedule.endDate && (
                      <Box
                        p={3}
                        bg="green.50"
                        rounded="md"
                        borderLeft="3px solid"
                        borderColor="green.400"
                        mb={3}
                      >
                        <HStack gap={2} align="start">
                          <Icon as={FiCalendar} color="green.600" boxSize={4} mt={0.5} />
                          <VStack align="start" gap={1}>
                            <Text fontSize="xs" color="green.800" fontWeight="medium">
                              Test duration set
                            </Text>
                            <Text fontSize="xs" color="green.700">
                              The test will automatically stop at the end date
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* Test Paths */}
              <ABTestPaths
                paths={abTestPaths}
                onChange={(paths) => onChange("abTestPaths", paths)}
                disabled={disabled}
                domain={domain}
                availablePaths={availablePaths}
                availableVariants={availableVariants}
                dropdownStates={abTestDropdowns}
                onDropdownChange={(pathId, state) => {
                  setAbTestDropdowns((prev) => ({
                    ...prev,
                    [pathId]: state
                  }));
                }}
              />
            </VStack>

            <Box
              p={3}
              bg="blue.50"
              rounded="md"
              borderLeft="3px solid"
              borderColor="blue.400"
            >
              <HStack gap={2} align="start">
                <Icon as={FiInfo} color="blue.600" boxSize={4} mt={0.5} />
                <VStack align="start" gap={1}>
                  <Text fontSize="xs" color="blue.800" fontWeight="medium">
                    Enhanced A/B Testing:
                  </Text>
                  <Text fontSize="xs" color="blue.700">
                    • Test multiple variations simultaneously
                  </Text>
                  <Text fontSize="xs" color="blue.700">
                    • Mix regular paths with variant IDs
                  </Text>
                  <Text fontSize="xs" color="blue.700">
                    • Schedule tests to run during specific periods
                  </Text>
                  <Text fontSize="xs" color="blue.700">
                    • Precise traffic distribution control
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </>
        )}

        {/* Custom URL */}
        {destinationType === "custom" && (
          <Box>
            <HStack mb={2}>
              <Text fontSize="sm" fontWeight="medium">
                Custom Redirect URL
              </Text>
              {customRedirectUrl && !urlValidation.isValid && (
                <HStack gap={1}>
                  <Icon as={FiAlertCircle} color="red.500" boxSize={4} />
                  <Text fontSize="xs" color="red.500">
                    {urlValidation.error}
                  </Text>
                </HStack>
              )}
              {customRedirectUrl && urlValidation.isValid && (
                <HStack gap={1}>
                  <Icon as={FiCheck} color="green.500" boxSize={4} />
                  <Text fontSize="xs" color="green.500">
                    Valid URL
                  </Text>
                </HStack>
              )}
            </HStack>
            <Input
              value={customRedirectUrl}
              onChange={(e) => {
                const value = e.target.value;
                onChange("customRedirectUrl", value);
                // Validate URL on change
                if (value) {
                  const validation = validateUrl(value);
                  setUrlValidation(validation);
                } else {
                  setUrlValidation({ isValid: true }); // Don't show error for empty field
                }
              }}
              placeholder="https://example.com/landing-page"
              disabled={disabled}
              borderColor={customRedirectUrl && !urlValidation.isValid ? "red.500" : undefined}
              _focus={{
                borderColor: customRedirectUrl && !urlValidation.isValid ? "red.500" : "blue.500",
                boxShadow: customRedirectUrl && !urlValidation.isValid ? "0 0 0 1px var(--chakra-colors-red-500)" : undefined
              }}
            />
            <Text fontSize="sm" color={customRedirectUrl && !urlValidation.isValid ? "red.500" : "gray.500"} mt={2}>
              {customRedirectUrl && !urlValidation.isValid 
                ? urlValidation.error 
                : "Enter the full URL where users will be redirected"}
            </Text>

            <Box
              mt={3}
              p={3}
              bg="amber.50"
              rounded="md"
              borderLeft="3px solid"
              borderColor="amber.400"
            >
              <HStack gap={2} align="start">
                <Icon as={FiInfo} color="amber.600" boxSize={4} mt={0.5} />
                <VStack align="start" gap={1}>
                  <Text fontSize="xs" color="amber.800" fontWeight="medium">
                    Custom URL Notes:
                  </Text>
                  <Text fontSize="xs" color="amber.700">
                    • Must be a valid, complete URL including protocol (https://)
                  </Text>
                  <Text fontSize="xs" color="amber.700">
                    • Can redirect to any external website or service
                  </Text>
                  <Text fontSize="xs" color="amber.700">
                    • Tracking parameters will be automatically appended
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

// Add spinning animation
if (typeof document !== 'undefined' && !document.querySelector('#destination-section-styles')) {
  const style = document.createElement("style");
  style.id = 'destination-section-styles';
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .animate-spin {
      animation: spin 1s linear infinite;
    }
  `;
  document.head.appendChild(style);
}